import {useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import {useIntl} from 'react-intl';
import {Lock, MicOff, Minus, MonitorOff, MonitorUp, Plus, RefreshCw, Scan, ShieldAlert, UserPlus, WifiLow, WifiOff, X} from 'lucide-react';
import {VideoTrack} from '@livekit/components-react';
import type {GroupCallState, ReceivedReaction, RingTargetState} from '@phonehq/rtc-core';
import {groupCallMessages as gm} from '../../groupCalls/messages';
import {groupCallService} from '../../groupCalls/services/groupCallService';
import {useCallMember} from '../../groupCalls/components/callContext';
import {AddPeoplePanel} from './AddPeoplePanel';
import {InviteeTile, InviteesSection} from './InviteesSection';
import {isRinging} from './invitees';
import {AloneCountdown} from './AloneCountdown';
import type {AloneAutoLeave} from './useAloneAutoLeave';
import {InviteBanner} from './GroupInvite';
import type {IncomingGroupCall} from '../../groupCalls/services/groupCallOrchestrator';
import type {CallPerson} from './useCallPeople';
import {ReactionsOverlay} from '../../groupCalls/components/ReactionsOverlay';
import {MinimizeButton, StageChip, StageControl, StageEndButton, StatusPill} from '../components/controls';
import {MemberAvatar} from '../components/MemberAvatar';
import {useElapsed} from '../hooks/useElapsed';
import {useEndedShare} from '../hooks/useEndedShare';
import type {GroupCallLeg} from '../model/types';
import {groupActions} from './groupActions';
import {GroupMoreMenu} from './GroupMoreMenu';
import {GroupTile} from './GroupTile';
import {fitTiles, useGroupTracks, type GroupScreenShare} from './useGroupTracks';
import {useElementSize} from '../hooks/useElementSize';
import type {ControlSpec, GroupControls} from './useGroupControls';
import {groupSurfaceMessages as m} from './groupMessages';
import {CallLayer} from '../components/CallLayer';

const WEAK = new Set(['poor', 'lost']);
const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

const subscribeReactions = (listener: (reaction: ReceivedReaction) => void) => groupCallService.onReaction(listener);

const StageHeadline = ({leg, waitingCount}: {leg: GroupCallLeg; waitingCount: number}) => {
    const intl = useIntl();
    const timer = useElapsed(leg.sessionStartedAt ?? leg.connectedAt);
    if (leg.network === 'reconnecting') {
        return (
            <div className="flex flex-col items-start gap-2">
                <StatusPill tone="reconnecting" icon={<RefreshCw size={16} className="animate-spin" aria-hidden="true"/>}>{intl.formatMessage(m.pillReconnecting)}</StatusPill>
                <p className="max-w-[360px] text-[13px] text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.pillReconnectingHelp)}</p>
            </div>
        );
    }
    if (leg.network === 'weak') {
        return (
            <div className="flex flex-col items-start gap-2">
                <StatusPill tone="weak" icon={<WifiLow size={16} aria-hidden="true"/>}>{intl.formatMessage(m.pillWeak)}</StatusPill>
                <p className="max-w-[360px] text-[13px] text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.pillWeakHelp)}</p>
            </div>
        );
    }
    if (leg.encryption !== 'ok') {
        return (
            <StageChip icon={<ShieldAlert size={14} aria-hidden="true"/>} testId="group-call-encryption-badge" status={leg.encryption}>
                {intl.formatMessage(leg.encryption === 'pending' ? m.chipSecuring : m.chipWarning)}
            </StageChip>
        );
    }
    const label = waitingCount > 0
        ? intl.formatMessage(timer ? m.chipWaitingTimer : m.chipWaiting, {count: waitingCount, timer})
        : timer ? intl.formatMessage(m.chipTeamCallTimer, {timer}) : intl.formatMessage(m.chipTeamCall);
    return (
        <StageChip icon={<Lock size={14} aria-hidden="true"/>} testId="group-call-encryption-badge" status="ok">
            {label}
        </StageChip>
    );
};

const GRID_GAP = 8;

const StageGrid = ({leg, waiting}: {leg: GroupCallLeg; waiting: readonly RingTargetState[]}) => {
    const {cameras, speaking} = useGroupTracks();
    const area = useRef<HTMLDivElement>(null);
    const size = useElementSize(area);
    const tile = fitTiles(cameras.length + waiting.length, size.width, size.height, GRID_GAP);
    const tileStyle = {width: tile.width, height: tile.height};
    const videoCall = leg.intent === 'video' || cameras.some(entry => entry.view.cameraEnabled);
    const dim = leg.network === 'reconnecting';
    return (
        <div
            ref={area}
            data-testid="group-call-grid"
            data-columns={tile.columns}
            aria-hidden={dim || undefined}
            className={`flex h-full w-full flex-wrap content-center items-center justify-center gap-2 overflow-hidden transition-opacity ${dim ? 'opacity-[.48]' : ''}`}
        >
            {tile.width > 0 && cameras.map(({trackRef, view}) => (
                <GroupTile
                    key={view.identity}
                    trackRef={trackRef}
                    view={view}
                    variant="stage"
                    speaking={speaking.has(view.identity)}
                    showCameraOff={videoCall}
                    style={tileStyle}
                    className="shrink-0"
                />
            ))}
            {tile.width > 0 && waiting.map(target => <InviteeTile key={target.matrixUserId} target={target} style={tileStyle} className="shrink-0"/>)}
        </div>
    );
};

const AloneView = ({leg, onAddPeople}: {leg: GroupCallLeg; onAddPeople?: () => void}) => {
    const intl = useIntl();
    const {cameras, speaking} = useGroupTracks();
    const local = cameras.find(tile => tile.view.isLocal);
    return (
        <div className="flex h-full flex-col items-center justify-center gap-5 text-center" data-testid="group-call-alone">
            {local && (
                <GroupTile
                    trackRef={local.trackRef}
                    view={local.view}
                    variant="stage"
                    speaking={speaking.has(local.view.identity)}
                    showCameraOff={leg.intent === 'video' || local.view.cameraEnabled}
                    className="aspect-video w-[320px] max-w-full"
                />
            )}
            <div className="flex flex-col gap-1">
                <p className="text-xl font-semibold">{intl.formatMessage(m.aloneTitle)}</p>
                <p className="max-w-[420px] text-sm text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.aloneBody)}</p>
            </div>
            {onAddPeople && (
                <button
                    type="button"
                    data-testid="group-call-alone-add-people"
                    onClick={onAddPeople}
                    className="phq-call-press inline-flex h-10 items-center gap-2 rounded-[10px] bg-[var(--phq-call-brand)] px-4 text-sm font-semibold text-white"
                >
                    <UserPlus size={16} aria-hidden="true"/>
                    {intl.formatMessage(m.addPeople)}
                </button>
            )}
        </div>
    );
};

const ZoomToolbar = ({zoom, onZoom}: {zoom: number; onZoom: (zoom: number) => void}) => {
    const intl = useIntl();
    const index = ZOOM_STEPS.findIndex(step => step === zoom);
    const button = 'phq-call-press inline-flex h-9 w-9 items-center justify-center rounded-lg text-[var(--phq-call-ink)] hover:bg-[var(--phq-call-surface-muted)] disabled:opacity-40';
    return (
        <div role="toolbar" aria-label={intl.formatMessage(m.zoomToolbar)} className="absolute bottom-3 right-3 flex items-center gap-0.5 rounded-xl bg-[var(--phq-call-overlay)] p-1 shadow-[inset_0_0_0_1px_var(--phq-call-line)]">
            <button type="button" className={button} aria-label={intl.formatMessage(m.zoomOut)} disabled={index <= 0} onClick={() => onZoom(ZOOM_STEPS[index - 1])}>
                <Minus size={18} aria-hidden="true"/>
            </button>
            <span className="min-w-12 text-center text-[13px] font-semibold tabular-nums">{Math.round(zoom * 100)}%</span>
            <button type="button" className={button} aria-label={intl.formatMessage(m.zoomIn)} disabled={index >= ZOOM_STEPS.length - 1} onClick={() => onZoom(ZOOM_STEPS[index + 1])}>
                <Plus size={18} aria-hidden="true"/>
            </button>
            <span className="mx-1 h-5 w-px bg-[var(--phq-call-line-strong)]"/>
            <button type="button" className={button} aria-label={intl.formatMessage(m.zoomFit)} onClick={() => onZoom(1)}>
                <Scan size={18} aria-hidden="true"/>
            </button>
        </div>
    );
};

const ShareWatch = ({screen}: {screen: GroupScreenShare}) => {
    const intl = useIntl();
    const profile = useCallMember(screen.view.userId);
    const {cameras, speaking} = useGroupTracks();
    const [zoom, setZoom] = useState(1);
    const paused = WEAK.has(screen.view.connectionQuality);
    return (
        <div className="flex h-full w-full gap-2">
            <div className="relative min-w-0 flex-1 overflow-hidden rounded-xl bg-[var(--phq-call-canvas)]">
                <div className="h-full w-full overflow-auto">
                    <div className="h-full w-full origin-center transition-transform" style={{transform: `scale(${zoom})`}}>
                        <VideoTrack trackRef={screen.trackRef} className={`h-full w-full object-contain ${paused ? 'brightness-[.45] grayscale' : ''}`}/>
                    </div>
                </div>
                {paused && (
                    <span className="absolute left-1/2 top-1/2 inline-flex h-11 -translate-x-1/2 -translate-y-1/2 items-center gap-2 rounded-xl bg-[rgba(9,9,11,.88)] px-4 text-sm font-semibold">
                        <WifiOff size={18} className="text-[var(--phq-call-warning-ink)]" aria-hidden="true"/>
                        {intl.formatMessage(m.sharingPaused)}
                    </span>
                )}
                <span className="absolute bottom-3 left-3 inline-flex h-7 items-center gap-1.5 rounded-lg bg-[var(--phq-call-chip-scrim)] px-2.5 text-[13px] font-semibold">
                    <MonitorUp size={14} aria-hidden="true"/>
                    {intl.formatMessage(gm.tileScreen, {name: profile.name})}
                </span>
                <ZoomToolbar zoom={zoom} onZoom={setZoom}/>
            </div>
            <div className="flex w-[200px] shrink-0 flex-col gap-2 overflow-y-auto">
                {cameras.map(({trackRef, view}) => (
                    <GroupTile key={view.identity} trackRef={trackRef} view={view} variant="film" speaking={speaking.has(view.identity)} className="h-[112px] w-[200px] shrink-0"/>
                ))}
            </div>
        </div>
    );
};

const ShareMine = ({audience}: {audience: number}) => {
    const intl = useIntl();
    return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 text-center">
            <div className="flex h-[220px] w-[360px] max-w-full items-center justify-center rounded-2xl bg-[var(--phq-call-surface)]">
                <MonitorUp size={64} className="text-[var(--phq-call-brand-focus)]" aria-hidden="true"/>
            </div>
            <p className="text-[15px] text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.sharingAudience, {count: audience})}</p>
        </div>
    );
};

const ShareEnded = ({name}: {name: string}) => {
    const intl = useIntl();
    return (
        <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-lg bg-[var(--phq-call-surface)] text-center">
            <MonitorOff size={40} className="text-[var(--phq-call-ink-subtle)]" aria-hidden="true"/>
            <p className="text-base font-semibold">{intl.formatMessage(m.sharingEnded, {name})}</p>
            <p className="text-sm text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.sharingEndedBody)}</p>
        </div>
    );
};

const EndedShareView = ({userId}: {userId: string}) => {
    const profile = useCallMember(userId);
    return <ShareEnded name={profile.name}/>;
};

interface PeopleAsideProps {
    readonly roomId?: string;
    readonly people: readonly CallPerson[];
    readonly invitees: readonly RingTargetState[];
    readonly addOpen: boolean;
    readonly onClose: () => void;
    readonly onAddPeople?: () => void;
    readonly onBackFromAdd: () => void;
}

const PersonRow = ({userId, isLocal, muted, reconnecting}: {userId: string; isLocal: boolean; muted: boolean; reconnecting: boolean}) => {
    const intl = useIntl();
    const profile = useCallMember(userId);
    return (
        <li className="flex h-12 items-center gap-3 px-4" data-testid="group-call-participant-row">
            <MemberAvatar userId={userId} name={profile.name} mxcAvatarUrl={profile.mxcAvatarUrl} size={32}/>
            <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{isLocal ? intl.formatMessage(m.peopleYou, {name: profile.name}) : profile.name}</p>
                {reconnecting && <p className="text-xs text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.peopleStatusReconnecting)}</p>}
            </div>
            {muted && <MicOff size={16} className="shrink-0 text-[var(--phq-call-dnd)]" aria-label={intl.formatMessage(gm.tileMuted)}/>}
            <span className="shrink-0 text-xs text-[var(--phq-call-ink-muted)]">{intl.formatMessage(m.peopleStatusInCall)}</span>
        </li>
    );
};

const PeopleAside = ({roomId, people, invitees, addOpen, onClose, onAddPeople, onBackFromAdd}: PeopleAsideProps) => {
    const intl = useIntl();
    const inCall = useMemo(() => new Set(people.map(person => person.userId)), [people]);
    return (
        <aside className="flex w-[340px] shrink-0 flex-col border-l border-[var(--phq-call-line)] bg-[var(--phq-call-surface)]" data-testid="group-call-participants-panel">
            {addOpen && roomId ? (
                <AddPeoplePanel roomId={roomId} peopleInCall={inCall} invitees={invitees} onBack={onBackFromAdd}/>
            ) : (
                <>
                    <header className="flex items-start justify-between px-4 pb-2 pt-5">
                        <div>
                            <h2 className="text-base font-semibold">{intl.formatMessage(m.peopleTitle)}</h2>
                            <p className="inline-flex items-center gap-1 text-xs text-[var(--phq-call-ink-muted)]">
                                <Lock size={12} aria-hidden="true"/>
                                {intl.formatMessage(m.peopleEncrypted)}
                            </p>
                        </div>
                        <button
                            type="button"
                            aria-label={intl.formatMessage(m.peopleClose)}
                            onClick={onClose}
                            className="phq-call-press inline-flex h-8 w-8 items-center justify-center rounded-lg text-[var(--phq-call-ink-muted)] hover:bg-[var(--phq-call-surface-muted)]"
                        >
                            <X size={18} aria-hidden="true"/>
                        </button>
                    </header>
                    <div className="min-h-0 flex-1 overflow-y-auto">
                        <p className="px-4 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-[.04em] text-[var(--phq-call-ink-muted)]">
                            {intl.formatMessage(m.peopleInCall, {count: people.length})}
                        </p>
                        <ul className="flex flex-col">
                            {people.map(person => (
                                <PersonRow key={person.userId} userId={person.userId} isLocal={person.isLocal} muted={person.muted} reconnecting={person.reconnecting}/>
                            ))}
                        </ul>
                        <InviteesSection invitees={invitees}/>
                    </div>
                    {onAddPeople && (
                        <div className="p-4">
                            <button
                                type="button"
                                data-testid="group-call-add-people-open"
                                onClick={onAddPeople}
                                className="phq-call-press flex h-10 w-full items-center justify-center gap-2 rounded-[10px] bg-[var(--phq-call-brand)] text-sm font-semibold text-white"
                            >
                                <UserPlus size={16} aria-hidden="true"/>
                                {intl.formatMessage(m.addPeople)}
                            </button>
                        </div>
                    )}
                </>
            )}
        </aside>
    );
};

const DockControl = ({spec}: {spec: ControlSpec}) => (
    <StageControl
        icon={spec.icon}
        label={spec.label}
        caption={spec.caption}
        tone={spec.tone}
        alert={spec.alert}
        badge={spec.badge}
        pressed={spec.pressed}
        hasPopup={spec.hasPopup}
        disabled={spec.disabled}
        testId={spec.testId}
        onClick={spec.onClick}
    />
);

interface GroupStageProps {
    readonly leg: GroupCallLeg;
    readonly state: GroupCallState;
    readonly controls: GroupControls;
    readonly handRaised: boolean;
    readonly peopleOpen: boolean;
    readonly addOpen: boolean;
    readonly people: readonly CallPerson[];
    readonly invitees: readonly RingTargetState[];
    readonly waiting: readonly RingTargetState[];
    readonly alone: boolean;
    readonly autoLeave?: AloneAutoLeave;
    readonly invite?: IncomingGroupCall;
    readonly onDeclineInvite?: () => void;
    readonly onAcceptInvite?: () => void;
    readonly onClosePeople: () => void;
    readonly onAddPeople?: () => void;
    readonly onBackFromAdd: () => void;
    readonly onPopOut?: () => void;
}

export const GroupStage = ({
    leg,
    state,
    controls,
    handRaised,
    peopleOpen,
    addOpen,
    people,
    invitees,
    waiting,
    alone,
    autoLeave,
    invite,
    onDeclineInvite,
    onAcceptInvite,
    onClosePeople,
    onAddPeople,
    onBackFromAdd,
    onPopOut,
}: GroupStageProps) => {
    const intl = useIntl();
    const {screen} = useGroupTracks();
    const remoteSharerId = screen && !screen.view.isLocal ? screen.view.userId : undefined;
    const endedShare = useEndedShare(remoteSharerId);
    const sharingMine = state.local.screenSharing && (!screen || screen.view.isLocal);
    const remoteShare = screen && !screen.view.isLocal ? screen : undefined;
    const audience = Math.max(0, leg.participantCount - 1);
    const region = useRef<HTMLElement>(null);

    useEffect(() => {
        region.current?.focus();
    }, []);

    const dock = [controls.microphone, controls.camera, sharingMine ? undefined : controls.share, controls.people, controls.add, controls.devices]
        .filter((spec): spec is ControlSpec => spec !== undefined);

    const content = remoteShare
        ? <ShareWatch screen={remoteShare}/>
        : sharingMine
            ? <ShareMine audience={audience}/>
            : endedShare
                ? <EndedShareView userId={endedShare}/>
                : alone && waiting.length === 0
                    ? <AloneView leg={leg} onAddPeople={peopleOpen ? undefined : onAddPeople}/>
                    : <StageGrid leg={leg} waiting={waiting}/>;

    return createPortal(
        <CallLayer>{
        <section
            ref={region}
            tabIndex={-1}
            aria-label={intl.formatMessage(m.teamCallRegion, {count: leg.participantCount})}
            data-testid="group-call-overlay"
            className="phq-call-ui phq-call-enter fixed bottom-0 left-14 right-0 top-0 z-[55] flex bg-[var(--phq-call-canvas)] [background-image:var(--phq-call-stage-gradient)] outline-none"
        >
            <div className="relative flex min-w-0 flex-1 flex-col px-4 pb-5 pt-[60px]" data-testid="group-call-in-call">
                <div className="absolute left-4 top-4 z-10 flex flex-col items-start gap-2">
                    {sharingMine ? (
                        <StatusPill tone="sharing" icon={<MonitorUp size={16} aria-hidden="true"/>}>{intl.formatMessage(m.sharingYou)}</StatusPill>
                    ) : (
                        <StageHeadline leg={leg} waitingCount={waiting.filter(isRinging).length}/>
                    )}
                    {autoLeave && <AloneCountdown autoLeave={autoLeave} className="rounded-full bg-[var(--phq-call-surface)] py-0.5 pl-3 pr-0.5"/>}
                </div>
                <div className="absolute right-4 top-3 z-10">
                    <MinimizeButton label={intl.formatMessage(m.minimize)} testId="group-call-minimize" onClick={groupActions.minimize}/>
                </div>
                {invite && onDeclineInvite && onAcceptInvite && (
                    <InviteBanner invite={invite} variant="stage" onDecline={onDeclineInvite} onLeaveAndJoin={onAcceptInvite}/>
                )}
                <main className="relative mb-4 min-h-0 flex-1">
                    {content}
                    <ReactionsOverlay subscribe={subscribeReactions}/>
                </main>
                <div className="flex items-start justify-center gap-4">
                    {dock.map(spec => <DockControl key={spec.id} spec={spec}/>)}
                    <GroupMoreMenu variant="stage" handRaised={handRaised} onPopOut={onPopOut}/>
                    <span className="w-4 shrink-0"/>
                    {sharingMine && (
                        <button
                            type="button"
                            data-testid="group-call-screen-share"
                            aria-label={intl.formatMessage(gm.controlStopSharing)}
                            onClick={groupActions.stopScreenShare}
                            className="phq-call-press inline-flex h-14 items-center gap-2 rounded-[20px] bg-[var(--phq-call-danger)] px-6 text-base font-semibold text-white"
                        >
                            <X size={22} aria-hidden="true"/>
                            {intl.formatMessage(gm.controlStopSharing)}
                        </button>
                    )}
                    {sharingMine ? (
                        <button
                            type="button"
                            data-testid="group-call-leave"
                            onClick={groupActions.leave}
                            className="phq-call-press inline-flex h-14 w-40 items-center justify-center rounded-[20px] bg-[var(--phq-call-surface-muted)] text-[15px] font-semibold"
                        >
                            {intl.formatMessage(gm.controlLeave)}
                        </button>
                    ) : (
                        <StageEndButton label={intl.formatMessage(gm.controlLeave)} testId="group-call-leave" onClick={groupActions.leave}/>
                    )}
                </div>
            </div>
            {peopleOpen && (
                <PeopleAside
                    roomId={state.roomId}
                    people={people}
                    invitees={invitees}
                    addOpen={addOpen}
                    onClose={onClosePeople}
                    onAddPeople={onAddPeople}
                    onBackFromAdd={onBackFromAdd}
                />
            )}
        </section>}</CallLayer>,
        document.body,
    );
};
